import React, { useEffect } from "react";
import { Button, Col, Divider, Drawer, Form, Input, Row, Select, Space, Upload, Typography, message, InputNumber } from "antd";
import { ExpandOutlined, CompressOutlined, DeleteOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useAuth } from "contexts/AuthContext";
import { useFileUpload } from "hooks/useFileUpload";
import useAppConfig from "hooks/access-control/useAppConfig";
import GooglePlacesAutocompleteInput from "components/common/GooglePlacesAutocompleteInput";
import { addressFieldsFromPlaceDetails } from "utils/googlePlaces";

export default function OrganizationDrawer({
  open,
  onClose,
  onSubmit,
  loading,
  initialValues,
  locationTypes = [],
  locationTypesLoading = false,
}) {
  const [form] = Form.useForm();
  const [fullWidth, setFullWidth] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const busy = loading || submitting;
  const { user } = useAuth();
  const { uploadFile } = useFileUpload();
  const { appConfig } = useAppConfig();
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey || appConfig?.googleMapsApiMatrixKey || "";
  const [certificateFiles, setCertificateFiles] = React.useState([]);
  const [logoFiles, setLogoFiles] = React.useState([]);
  const [profilePhotoFiles, setProfilePhotoFiles] = React.useState([]);
  const [otherDocFiles, setOtherDocFiles] = React.useState([]);
  const [certificateUploading, setCertificateUploading] = React.useState(false);
  const [logoUploading, setLogoUploading] = React.useState(false);
  const [profilePhotoUploading, setProfilePhotoUploading] = React.useState(false);
  const [otherDocsUploading, setOtherDocsUploading] = React.useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setCertificateFiles([]);
      setLogoFiles([]);
      setProfilePhotoFiles([]);
      setOtherDocFiles([]);
      if (initialValues) {
        form.setFieldsValue({
          ...initialValues,
          contactInformation: initialValues.contactInformation || {},
          locationInformation: initialValues.locationInformation || {},
          organizationLocationType:
            initialValues.organizationLocationType?.id ||
            initialValues.organizationLocationTypeId ||
            initialValues.organizationLocationType?.code ||
            initialValues.organizationLocationType ||
            null,
        });

        if (initialValues.certificate) {
          setCertificateFiles([
            { uid: String(initialValues.certificate), name: "certificate", status: "done" },
          ]);
        }
        if (initialValues.logo) {
          setLogoFiles([{ uid: String(initialValues.logo), name: "logo", status: "done" }]);
        }
        if (initialValues.profilePhoto) {
          setProfilePhotoFiles([
            { uid: String(initialValues.profilePhoto), name: "profile photo", status: "done" },
          ]);
        }
        if (Array.isArray(initialValues.otherDocs) && initialValues.otherDocs.length) {
          setOtherDocFiles(
            initialValues.otherDocs.map((id, idx) => ({
              uid: String(id),
              name: `doc-${idx + 1}`,
              status: "done",
            })),
          );
        }
      }
    }
  }, [open, form, initialValues]);

  const ensureUserId = () => user?.id;

  const uploadToField = async (
    { file, onSuccess, onError },
    fieldName,
    fileType,
    setUploading,
    setFiles,
  ) => {
    try {
      setUploading?.(true);
      const userId = ensureUserId();
      if (!userId) throw new Error("Missing user id");
      const res = await uploadFile(userId, file, { purpose: "USER_PROFILE", fileType });
      form.setFieldValue(fieldName, res.fileId);
      setFiles?.([{ uid: String(res.fileId), name: file?.name || fieldName, status: "done" }]);
      onSuccess?.(res, file);
      message.success(`${file?.name || "File"} uploaded`);
      toast.success("File uploaded");
    } catch (err) {
      onError?.(err);
      message.error(err?.message || "Failed to upload file");
      toast.error(err?.message || "Failed to upload file");
    } finally {
      setUploading?.(false);
    }
  };

  const uploadToOtherDocs = async ({ file, onSuccess, onError }, fileType) => {
    try {
      setOtherDocsUploading(true);
      const userId = ensureUserId();
      if (!userId) throw new Error("Missing user id");
      const res = await uploadFile(userId, file, { purpose: "USER_PROFILE", fileType });
      const existing = form.getFieldValue("otherDocs");
      const next = Array.isArray(existing) ? [...existing, res.fileId] : [res.fileId];
      form.setFieldValue("otherDocs", next);
      setOtherDocFiles((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        { uid: String(res.fileId), name: file?.name || "document", status: "done" },
      ]);
      onSuccess?.(res, file);
      message.success(`${file?.name || "File"} uploaded`);
      toast.success("File uploaded");
    } catch (err) {
      onError?.(err);
      message.error(err?.message || "Failed to upload file");
      toast.error(err?.message || "Failed to upload file");
    } finally {
      setOtherDocsUploading(false);
    }
  };

  const handleFinish = async (values) => {
    const payload = {
      ...values,
      certificate: form.getFieldValue("certificate") || null,
      logo: form.getFieldValue("logo") || null,
      profilePhoto: form.getFieldValue("profilePhoto") || null,
      otherDocs: Array.isArray(form.getFieldValue("otherDocs"))
        ? form.getFieldValue("otherDocs")
        : [],
      contactInformation: values.contactInformation || null,
      locationInformation: values.locationInformation || null,
    };
    try {
      setSubmitting(true);
      await onSubmit(payload);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[OrganizationDrawer] submit failed", {
        status: err?.response?.status,
        url: err?.config?.url,
        method: err?.config?.method,
        responseData: err?.response?.data,
        message: err?.message,
      });
      const errorData = err?.response?.data || err;
      const errors = errorData?.errors;

      if (errors && typeof errors === "object") {
        const fieldErrors = Object.entries(errors)
          .filter(([, msg]) => Boolean(msg))
          .map(([field, msg]) => ({
            name: String(field)
              .split(".")
              .filter(Boolean),
            errors: [String(msg)],
          }));

        if (fieldErrors.length) form.setFields(fieldErrors);

        const messages = Object.entries(errors)
          .map(([field, msg]) => `${field}: ${msg}`)
          .join("\n");
        toast.error(`Validation failed:\n${messages}`);
        return;
      }

      toast.error(errorData?.message || err?.message || "Failed to save organization");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title={
        <Space>
          <span>
            {initialValues?.id ? "Edit Organization" : "Create Organization"}
          </span>
        </Space>
      }
      width={fullWidth ? "100vw" : 720}
      styles={{
        body: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
        header: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
        footer: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
      }}
      open={open}
      onClose={() => {
        if (!busy) onClose?.();
      }}
      closable={!busy}
      maskClosable={!busy}
      keyboard={!busy}
      destroyOnClose
      placement="right"
      zIndex={2000}
      extra={
        <Space>
          <Button
            type="text"
            aria-label={fullWidth ? "Normal drawer width" : "Expand drawer"}
            title={fullWidth ? "Normal width" : "Expand"}
            icon={fullWidth ? <CompressOutlined /> : <ExpandOutlined />}
            disabled={busy}
            onClick={() => setFullWidth((v) => !v)}
          />
        </Space>
      }
      footer={
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button disabled={busy} onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={busy}
            disabled={busy}
            onClick={() => form.submit()}
          >
            Save
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
      >
        <Row gutter={12}>
          <Col span={16}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Name is required" }]}
            >
              <Input placeholder="Organization name" />
            </Form.Item>
          </Col>
          <Col span={8} />
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="ein" label="EIN">
              <Input placeholder="Employer Identification Number" />
            </Form.Item>
          </Col>
          <Col span={12} />
        </Row>

        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} placeholder="Short description" />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="organizationLocationType"
              label="Organization Location Type"
              rules={[{ required: true, message: "Organization location type is required" }]}
            >
              <Select
                placeholder="Select location type"
                loading={locationTypesLoading}
                allowClear
                showSearch
                optionFilterProp="label"
                options={locationTypes.map((t) => ({
                  value: t.id,
                  label: `${t.label} (${t.code})`,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: "12px 0" }} />
        <Typography.Text strong>Files</Typography.Text>
        <Form.Item name="certificate" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="logo" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="profilePhoto" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="otherDocs" hidden>
          <Input />
        </Form.Item>
        <Row gutter={12} style={{ marginTop: 8 }}>
          <Col span={12}>
            <Form.Item label="Certificate">
              <Upload
                fileList={certificateFiles}
                maxCount={1}
                showUploadList={{
                  showRemoveIcon: true,
                  showPreviewIcon: false,
                  removeIcon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
                }}
                customRequest={(opts) =>
                  uploadToField(
                    opts,
                    "certificate",
                    "DOCUMENT",
                    setCertificateUploading,
                    setCertificateFiles,
                  )
                }
                onChange={({ fileList }) => setCertificateFiles(fileList)}
                onRemove={() => {
                  form.setFieldValue("certificate", null);
                  setCertificateFiles([]);
                  return true;
                }}
              >
                <Button loading={certificateUploading}>Upload</Button>
              </Upload>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Logo">
              <Upload
                fileList={logoFiles}
                maxCount={1}
                accept="image/*"
                showUploadList={{
                  showRemoveIcon: true,
                  showPreviewIcon: false,
                  removeIcon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
                }}
                customRequest={(opts) =>
                  uploadToField(opts, "logo", "PHOTO", setLogoUploading, setLogoFiles)
                }
                onChange={({ fileList }) => setLogoFiles(fileList)}
                onRemove={() => {
                  form.setFieldValue("logo", null);
                  setLogoFiles([]);
                  return true;
                }}
              >
                <Button loading={logoUploading}>Upload</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Profile Photo">
              <Upload
                fileList={profilePhotoFiles}
                maxCount={1}
                accept="image/*"
                showUploadList={{
                  showRemoveIcon: true,
                  showPreviewIcon: false,
                  removeIcon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
                }}
                customRequest={(opts) =>
                  uploadToField(
                    opts,
                    "profilePhoto",
                    "PHOTO",
                    setProfilePhotoUploading,
                    setProfilePhotoFiles,
                  )
                }
                onChange={({ fileList }) => setProfilePhotoFiles(fileList)}
                onRemove={() => {
                  form.setFieldValue("profilePhoto", null);
                  setProfilePhotoFiles([]);
                  return true;
                }}
              >
                <Button loading={profilePhotoUploading}>Upload</Button>
              </Upload>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Other Docs">
              <Upload
                fileList={otherDocFiles}
                multiple
                showUploadList={{
                  showRemoveIcon: true,
                  showPreviewIcon: false,
                  removeIcon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
                }}
                customRequest={(opts) => uploadToOtherDocs(opts, "DOCUMENT")}
                onChange={({ fileList }) => setOtherDocFiles(fileList)}
                onRemove={(file) => {
                  const existing = form.getFieldValue("otherDocs");
                  const next = Array.isArray(existing)
                    ? existing.filter((id) => String(id) !== String(file.uid))
                    : [];
                  form.setFieldValue("otherDocs", next);
                  setOtherDocFiles((prev) =>
                    Array.isArray(prev) ? prev.filter((f) => f.uid !== file.uid) : [],
                  );
                  return true;
                }}
              >
                <Button loading={otherDocsUploading}>Upload</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <h4 style={{ marginTop: 16 }}>Contact Information</h4>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name={["contactInformation", "primaryEmail"]}
              label="Primary Email"
              rules={[{ type: "email", message: "Invalid email" }]}
            >
              <Input placeholder="email@domain.com" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name={["contactInformation", "primaryPhone"]} label="Primary Phone">
              <Input placeholder="+1..." />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name={["contactInformation", "secondaryEmail"]}
              label="Secondary Email"
              rules={[{ type: "email", message: "Invalid email" }]}
            >
              <Input placeholder="email@domain.com" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name={["contactInformation", "secondaryPhone"]} label="Secondary Phone">
              <Input placeholder="+1..." />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name={["contactInformation", "websiteUrl"]} label="Website">
              <Input placeholder="https://..." />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name={["contactInformation", "contactPersonTitle"]} label="Contact Person Title">
              <Input placeholder="Owner / Manager" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name={["contactInformation", "contactPersonName"]} label="Contact Person Name">
              <Input placeholder="Full name" />
            </Form.Item>
          </Col>
        </Row>

        <h4 style={{ marginTop: 16 }}>Location Information</h4>
        <div style={{ marginBottom: 12 }}>
          <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
            Search and select an address to auto-fill location fields.
          </Typography.Text>
          <GooglePlacesAutocompleteInput
            googleMapsApiKey={googleMapsApiKey}
            placeholder="Search for an address"
            onResolvePlace={async ({ details }) => {
              const next = addressFieldsFromPlaceDetails(details);
              if (next.addressLine1)
                form.setFieldValue(["locationInformation", "addressLine1"], next.addressLine1);
              if (next.city) form.setFieldValue(["locationInformation", "city"], next.city);
              if (next.state) form.setFieldValue(["locationInformation", "state"], next.state);
              if (next.zipCode) form.setFieldValue(["locationInformation", "zipCode"], next.zipCode);
              if (next.county) form.setFieldValue(["locationInformation", "county"], next.county);
              if (next.country) form.setFieldValue(["locationInformation", "country"], next.country);
              if (typeof next.latitude === "number")
                form.setFieldValue(["locationInformation", "latitude"], next.latitude);
              if (typeof next.longitude === "number")
                form.setFieldValue(["locationInformation", "longitude"], next.longitude);
              if (next.placeId)
                form.setFieldValue(["locationInformation", "placeId"], next.placeId);
            }}
          />
        </div>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name={["locationInformation", "addressLine1"]} label="Address Line 1">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name={["locationInformation", "addressLine2"]} label="Address Line 2">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name={["locationInformation", "city"]} label="City">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={["locationInformation", "state"]} label="State">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={["locationInformation", "zipCode"]} label="Zip Code">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name={["locationInformation", "county"]} label="County">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={["locationInformation", "country"]} label="Country">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name={["locationInformation", "latitude"]} label="Latitude">
              <InputNumber style={{ width: "100%" }} placeholder="e.g. 47.6062" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={["locationInformation", "longitude"]} label="Longitude">
              <InputNumber style={{ width: "100%" }} placeholder="e.g. -122.3321" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={["locationInformation", "placeId"]} label="Place ID">
              <Input placeholder="Google Place ID" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  );
}
